import { inRange, isInteger } from 'lodash-es';

export function zeroPadInt(n = 0, padding = 2) {
  return n.toString().padStart(padding, '0');
}

const TIME_REGEX = /([012]?\d):([012345]\d)([aApP][mM])/;

/**
 * A partial time in hours and minutes. Hours are 1-12, minutes are 0-59.
 * Values outside of those given ranges are considered invalid and unparsable.
 */
export interface PartialTime {
  hours?: number;
  minutes?: number;
}
/*
13 ->  1pm
 0 -> 12am
12 -> 12pm
24 -> 12am
*/

export const partialTimeToString = (partialTime: PartialTime) => {
  try {
    const { hours, minutes } = partialTime;
    let hoursString;

    if (hours == undefined || Number.isNaN(hours)) {
      hoursString = '';
    } else if (hours > 12) {
      const modHours = hours % 12;

      hoursString = modHours === 0 ? 12 : modHours;
    } else {
      hoursString = hours.toString();
    }

    const minutesString = zeroPadInt(minutes);
    const period = (hours || 0) > 12 ? 'PM' : 'AM'; // undefined is neither greater than, equal to, or less than any number

    return `${hoursString}:${minutesString}${period}`;
  } catch (err) {
    return '';
  }
};

/**
 * Check that a `PartialTime` has all necessary data.
 *
 * A `PartialTime` is valid if:
 * * it has an `hours` property set to a number between 0 and 23 inclusive
 * * it has an `minutes` property set to a number between 0 and 59 inclusive
 * * It has a period ('am' or 'pm')
 */
export const isPartialTimeValid = (partialTime: PartialTime) =>
  isInteger(partialTime?.hours) &&
  inRange(partialTime?.hours || -1, 0, 23) &&
  isInteger(partialTime?.minutes) &&
  inRange(partialTime?.minutes || -1, 0, 60);

type PartialTimeFromStringReturnType =
  | { isValid: true; timeObject: PartialTime }
  | { isValid: false; timeObject: undefined };

/**
 * Convert a given string in 'hh:mmA' format to a `PartialTime`
 * If it can't be converted, the returned object will reflect that
 * @param timeString The string to be converted into a `PartialTime`
 */
export const partialTimeFromString = (timeString = ''): PartialTimeFromStringReturnType => {
  const [, hoursString, minutesString, period] = timeString.match(TIME_REGEX) ?? ['', '', ''];

  if (period === undefined) {
    return {
      timeObject: undefined,
      isValid: false,
    };
  }

  const [hours, minutes] = [hoursString, minutesString].map((string) => parseInt(string));

  let periodHoursModifier = 0;

  if (period.toLowerCase() === 'pm') {
    periodHoursModifier = 12;
  }

  const timeObject: PartialTime = {
    hours: hours + periodHoursModifier,
    minutes,
  };

  if (isPartialTimeValid(timeObject)) {
    return {
      timeObject,
      isValid: true,
    };
  }

  // The PartialTime was invalid for some reason, so we emit undefined instead
  return {
    timeObject: undefined,
    isValid: false,
  };
};
